import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { useDropzone, ErrorCode } from 'react-dropzone'
import VideoSVG from 'assets/icon/video-upload.svg'
import ImageSVG from 'assets/icon/image-upload.svg'
import NotAccepted from 'assets/icon/cross.png'
import {
  MAX_UPLOADED_IMAGE_SIZE,
  MAX_UPLOADED_IMAGES,
  MAX_UPLOADED_VIDEO_SIZE,
  MAX_UPLOADED_VIDEOS,
} from 'constants/upload'
import { useLiveStates } from 'hooks'
import { Button } from 'components/UI'
import { getAvatarUrl } from '../../helpers'

const DropZone = ({ type, isUploaded }) => {
  const [customErrorTooMany, setCustomErrorTooMany] = useState(false)
  const { watch, setValue } = useFormContext()
  const files = watch(type) // type as form name
  const videoRef = useRef()
  const liveState = useLiveStates({
    files,
    type,
    isUploaded,
  })

  const { getRootProps, getInputProps, open, isDragActive, fileRejections } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: type === 'images' ? MAX_UPLOADED_IMAGE_SIZE : MAX_UPLOADED_VIDEO_SIZE,
    accept:
      type === 'images'
        ? {
            'image/*': ['.webp', '.jpeg', '.png', '.gif', '.svg'],
            // 'image/png': ['.png'],
            // 'image/jpeg': ['.jpg', '.jpeg'],
            // 'image/webp': ['.webp'],
            // 'image/gif': ['.gif'],
            // 'image/svg+xml': ['.svg'],
          }
        : {
            'video/mp4': ['.mp4', '.MP4', '.avi'],
          },
    maxFiles: type === 'images' ? MAX_UPLOADED_IMAGES : MAX_UPLOADED_VIDEOS,
    onDrop: (acceptedFiles) => {
      if (type === 'images') {
        if (files.length + acceptedFiles.length > MAX_UPLOADED_IMAGES) {
          setCustomErrorTooMany(true)
        } else {
          setCustomErrorTooMany(false)
          setValue(
            type,
            files.concat(
              acceptedFiles.map((file) =>
                Object.assign(file, {
                  preview: URL.createObjectURL(file),
                })
              )
            )
          )
        }
      } else {
        if (acceptedFiles[0]) {
          setValue(type, [
            Object.assign(acceptedFiles[0], {
              preview: URL.createObjectURL(acceptedFiles[0]),
            }),
          ])
        }
      }
    },
  })

  const resetURLState = () => {
    liveState.current.files.forEach((file) => URL.revokeObjectURL(file.preview))
    setValue(liveState.current.type, [])
  }

  const removeVideo = () => {
    resetURLState()
  }

  const renderErrors = useMemo(() => {
    if (
      customErrorTooMany ||
      fileRejections.some((f) => f.errors.some((e) => e.code === ErrorCode.TooManyFiles))
    ) {
      return <p className="text-sm text-rose-500 mt-1">Too many files</p>
    } else {
      return (
        <ul className="mt-1">
          {fileRejections.map(({ file, errors }) => (
            <li key={file.path}>
              <p className="flex gap-2 items-center text-sm text-rose-500 font-semibold">
                <img src={NotAccepted} alt="not-accepted" />
                {file.name}
              </p>
              <ul className="pl-6">
                {errors.map((e) => (
                  <li key={e.code} className="text-sm text-rose-500">
                    {e.message}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )
    }
  }, [fileRejections])

  // useEffect(() => {
  //   if (liveState.current.type === 'video' && files.length && videoRef.current) {
  //     videoRef.current.src = files[0].preview
  //     // videoRef.current.load()
  //   }
  // }, [files])

  useEffect(
    () => () => {
      // Make sure to revoke the Object URL to avoid memory leaks
      if (!liveState.current.isUploaded) {
        resetURLState()
      }
    },
    []
  )

  return (
    <div>
      <div className="h-[420px]">
        {files.length ? (
          type === 'video' ? (
            <div className="h-full flex flex-col">
              <video src={files[0]?.preview} controls className="flex-1 w-full rounded-2">
                {/* <source ref={videoRef} /> */}
              </video>
              <div className="flex items-center mt-1 justify-between">
                <p className="text-sm text-[#F67443]">{files[0]?.name}</p>
                <Button
                  className="bg-transparent !text-[#F67443] text-sm flex items-center gap-2"
                  onClick={removeVideo}
                >
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.73057 5.01081H12.5908V13.8347C12.5908 14.0148 12.5193 14.1874 12.392 14.3147C12.2647 14.442 12.092 14.5135 11.912 14.5135H2.40934C2.22932 14.5135 2.05667 14.442 1.92938 14.3147C1.80209 14.1874 1.73057 14.0148 1.73057 13.8347V5.01081ZM3.0881 6.36834V13.156H11.2333V6.36834H3.0881ZM5.12439 7.72587H6.48192V11.7984H5.12439V7.72587ZM7.83945 7.72587H9.19697V11.7984H7.83945V7.72587ZM3.76686 2.97452V1.617C3.76686 1.43698 3.83838 1.26433 3.96567 1.13704C4.09296 1.00974 4.26561 0.938232 4.44563 0.938232H9.87574C10.0558 0.938232 10.2284 1.00974 10.3557 1.13704C10.483 1.26433 10.5545 1.43698 10.5545 1.617V2.97452H13.9483V4.33205H0.373047V2.97452H3.76686ZM5.12439 2.29576V2.97452H9.19697V2.29576H5.12439Z"
                      fill="#F67443"
                    />
                  </svg>
                  remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-center items-center">
              <img
                src={
                  files[files.length - 1]?.preview ||
                  (files[files.length - 1]?.original
                    ? getAvatarUrl(files[files.length - 1].original)
                    : '')
                }
                alt="Preview"
                className="max-h-full object-contain w-full rounded-2"
              />
            </div>
          )
        ) : (
          <div
            className={`h-full bg-white flex justify-center items-center flex-col border-2 rounded-4 !border-[#F79009] ${isDragActive ? '!border-solid' : '!border-dashed'}`}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <img src={type === 'video' ? VideoSVG : ImageSVG} alt="Svg" className="mb-4" />
            <p className="text-[#F67443] mb-2">
              Drop your {type === 'video' ? 'video' : 'image'} here or&nbsp;
              <span className="font-semibold cursor-pointer text-2xl" onClick={open}>
                browse
              </span>
            </p>
            <p className="text-[#F79009] text-xs">
              {type === 'video'
                ? 'Supports: MP4, MPG, MPEG, and MOV formats'
                : 'Supports: JPG, PNG, and GIF formats'}
            </p>
          </div>
        )}
      </div>
      {renderErrors}
    </div>
  )
}

DropZone.propTypes = {
  type: PropTypes.oneOf(['video', 'images']).isRequired,
  isUploaded: PropTypes.bool,
}

export default React.memo(DropZone)
